import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, LogOut, Menu, Moon, RefreshCw, Search, Settings, Sun } from 'lucide-react'
import type { RefObject } from 'react'

import { useAuth } from '@/lib/auth/use-auth'
import { useTheme } from '@/lib/theme/use-theme'
import { cn } from '@/lib/utils'

export interface TopbarProps {
  title: string
  /** A back target for a detail screen; renders a back arrow before the title. */
  backTo?: string | null
  onBack?: (() => void) | undefined
  /** Opens the command palette — what the search box and the `/` key both do. */
  onOpenSearch: () => void
  /** Only rendered below 768px, where the sidebar is an overlay drawer. */
  showMenuButton: boolean
  onOpenMenu: () => void
  menuButtonRef: RefObject<HTMLButtonElement | null>
  /** The current company, shown when the sidebar is too narrow to name it. */
  orgName?: string | undefined
}

const ICON_BUTTON =
  'grid size-9 shrink-0 place-items-center rounded-control text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg'

function UserMenu() {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const email = user?.email ?? ''
  const initial = (email[0] ?? '?').toUpperCase()

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={email ? `Account menu for ${email}` : 'Account menu'}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-sm font-semibold text-fg-on-accent transition-opacity hover:opacity-90"
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-control border border-border bg-surface-raised py-1 shadow-lg"
        >
          <p className="truncate px-3 py-2 text-xs text-fg-muted" title={email}>
            {email || 'Signed in'}
          </p>
          <div className="my-1 border-t border-border" />
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-fg transition-colors hover:bg-surface-hover"
          >
            <Settings className="size-4 text-fg-muted" aria-hidden />
            Settings
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
          >
            <LogOut className="size-4 text-fg-muted" aria-hidden />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * The fixed header: page title and company on the left, global tools on the right.
 *
 * The tools are deliberately in one row rather than behind an overflow menu —
 * refresh and search are used constantly during data entry. Nothing page-specific
 * lives here: this row is a fixed 64px, and a page's actions vary in both number
 * and width, so they get their own wrapping row from the shell instead.
 */
export function Topbar({
  title,
  backTo,
  onBack,
  onOpenSearch,
  showMenuButton,
  onOpenMenu,
  menuButtonRef,
  orgName,
}: TopbarProps) {
  const { theme, toggleTheme } = useTheme()
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)

  const refresh = async () => {
    setRefreshing(true)
    try {
      // Everything on screen is server state; refetching the lot is what the
      // button promises, and TanStack dedupes the requests.
      await queryClient.invalidateQueries()
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-surface px-4">
      {showMenuButton && (
        <button
          ref={menuButtonRef}
          type="button"
          onClick={onOpenMenu}
          aria-label="Open navigation"
          className={ICON_BUTTON}
        >
          <Menu className="size-5" aria-hidden />
        </button>
      )}

      {backTo &&
        (onBack ? (
          <button type="button" onClick={onBack} aria-label="Back" className={ICON_BUTTON}>
            <ArrowLeft className="size-5" aria-hidden />
          </button>
        ) : (
          <Link to={backTo} aria-label="Back" className={ICON_BUTTON}>
            <ArrowLeft className="size-5" aria-hidden />
          </Link>
        ))}

      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="truncate text-base font-semibold tracking-tight text-fg">{title}</h1>
        {orgName && (
          <>
            <span aria-hidden className="text-fg-subtle">
              ·
            </span>
            <span className="min-w-0 truncate text-sm text-fg-secondary" title={orgName}>
              {orgName}
            </span>
          </>
        )}
      </div>

      <div className="flex-1" />

      <button
        type="button"
        onClick={() => void refresh()}
        disabled={refreshing}
        aria-label="Refresh data"
        className={ICON_BUTTON}
      >
        <RefreshCw className={cn('size-5', refreshing && 'animate-spin')} aria-hidden />
      </button>

      {/* Below md the same palette is reachable as an icon. Without it a phone
          has no search at all: the full box is hidden and `/` needs a keyboard. */}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Search and jump to…"
        className={cn(ICON_BUTTON, 'md:hidden')}
      >
        <Search className="size-5" aria-hidden />
      </button>

      {/* Opens the command palette — jump to any screen. A button, not an input:
          the palette is the real search surface, so this only needs to summon it. */}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Search and jump to…"
        className="relative hidden h-9 w-56 items-center rounded-control border border-border-strong bg-surface-sunken pl-9 pr-10 text-left text-sm text-fg-subtle transition-colors hover:border-accent md:flex lg:w-72"
      >
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted"
          aria-hidden
        />
        Search…
        <kbd
          aria-hidden
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-fg-muted"
        >
          /
        </kbd>
      </button>

      <button
        type="button"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        className={ICON_BUTTON}
      >
        {theme === 'dark' ? (
          <Sun className="size-5" aria-hidden />
        ) : (
          <Moon className="size-5" aria-hidden />
        )}
      </button>

      <UserMenu />
    </header>
  )
}
